import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import { thumbnailOverflowWrapper } from '@pkg/components/Thumbnail/tokens';
import {
    floorGameColor as color,
    floorGameDuration as duration,
    floorGameEasing as easing,
    styledInteraction,
    styledReset,
    styledTypography,
} from '@pkg/primitives';

import { noticeStyle, noticeTransition, NoticeVariant, noticeVariants } from '../../NftCardNotification.motion';
import { tokens } from '../../tokens';
import { NftCardNotificationEvent } from '../../types';

interface StyledNoticeProps {
    $event?: NftCardNotificationEvent;
    $compact?: boolean;
}

export const StyledNotice = styled(motion.div).attrs({
    variants: noticeVariants,
    initial: NoticeVariant.Initial,
    animate: NoticeVariant.Show,
    exit: NoticeVariant.Exit,
    transition: noticeTransition,
    style: noticeStyle,
    layout: true,
})<StyledNoticeProps>`
    --yat_nft-card-notice-bg-color: ${tokens.rgba.black40};
    --yat_nft-card-notice-bg-color-start: ${tokens.rgba.void};
    --yat_nft-card-notice-bg-color-end: ${tokens.rgba.black60};
    --yat_nft-card-notice-glow-color: ${tokens.rgba.black60};

    --yat_nft-card-notice-thumbnail-size: ${({ $compact }) =>
        tokens.notice.thumbnail.size[$compact ? 'compact' : 'default']}px;
    --yat_nft-card-notice-font-size: ${({ $compact }) =>
        tokens.notice.message.fontSize[$compact ? 'compact' : 'default']}px;
    --yat_nft-card-notice-suffix-size: ${({ $compact }) =>
        tokens.notice.suffix.size[$compact ? 'compact' : 'default']}px;

    ${({ $event }) => {
        if ($event === NftCardNotificationEvent.Burned) {
            return css`
                --yat_nft-card-notice-bg-color-start: ${tokens.rgba.red};
                --yat_nft-card-notice-bg-color-end: ${tokens.rgba.pink};
                --yat_nft-card-notice-glow-color: ${tokens.rgba.rust};
            `;
        }

        if ($event === NftCardNotificationEvent.Listed) {
            return css`
                --yat_nft-card-notice-bg-color-start: ${tokens.rgba.lime};
                --yat_nft-card-notice-bg-color-end: ${tokens.rgba.cyan};
                --yat_nft-card-notice-glow-color: ${tokens.rgba.forest};
            `;
        }
    }};
`;

const interiorBase = css`
    background-color: var(--yat_nft-card-notice-bg-color);
    background-image: linear-gradient(
        190deg,
        var(--yat_nft-card-notice-bg-color-start),
        var(--yat_nft-card-notice-bg-color-end)
    );
    border-radius: ${tokens.notice.interior.radius}px;
    box-shadow: 2px 6px 12px var(--yat_nft-card-notice-glow-color);

    @supports (backdrop-filter: blur(1px)) {
        backdrop-filter: blur(20px);
    }
`;

export const PlainInterior = styled.div`
    ${interiorBase};
`;

export const ActionInterior = styled.button.attrs({ type: 'button' })`
    ${styledReset.button};
    ${interiorBase};

    ${styledInteraction.focusRing({
        offset: -tokens.notice.interior.focusRing.offset,
        radius: tokens.notice.interior.focusRing.radius,
    })};

    text-align: left;
    transition: background-color ${duration.normal}ms ${easing.cubic};

    &:focus,
    &:hover {
        --yat_nft-card-notice-bg-color: ${tokens.rgba.black};
    }

    &:active {
        --yat_nft-card-notice-bg-color: ${tokens.rgba.black60};
    }
`;

export const Content = styled.div`
    display: flex;
    align-content: center;
    align-items: center;
    gap: ${tokens.notice.content.gap}px;
    padding: ${tokens.notice.content.padding}px;
    // Add a little extra space to the end.
    padding-right: ${tokens.notice.content.gap}px;
`;

export const ThumbnailWrapper = styled.div`
    ${thumbnailOverflowWrapper(tokens.notice.thumbnail.radius)};
    flex: 0 0 auto;
    width: var(--yat_nft-card-notice-thumbnail-size);
    height: var(--yat_nft-card-notice-thumbnail-size);
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
`;

export const Message = styled.p`
    ${styledTypography.allianceBase};
    flex: 1 1 auto;
    font-size: var(--yat_nft-card-notice-font-size);
    font-weight: 700;
    text-shadow: 0 0 6px rgba(0, 0, 0, 0.2);
    letter-spacing: -0.02em;
    color: ${color.white};
`;

export const MessageLabel = styled.span`
    opacity: 0.6;
`;

export const MessageValue = styled.span``;

export const Suffix = styled.p`
    ${styledTypography.allianceBase};
    flex: 0 0 auto;
    font-size: var(--yat_nft-card-notice-suffix-size);
`;
